<template>
  <div
    class="payment-term p-3 mb-3"
    :class="selected ? 'active' : ''"
    @click="selectTerm"
  >
    <div
      v-if="badgeType !== badgeTypes.NO_PAYMENT_BADGE"
      class="payment-term-badge"
    >
      <div class="badge-title-background">
        <span
          v-if="badgeType === badgeTypes.LOWER_WEEKLY_PAYMENT_BADGES"
          class="badge-title"
        >
          {{
            t("LOWER_WEEKLY_PAYMENT_BADGES", {
              currency: currency.sign,
              val: formatPrice(badgeValue),
            })
          }}
        </span>
        <span
          v-if="badgeType === badgeTypes.MONTH_TERM_POPULARITY_BADGES"
          class="badge-title"
        >
          {{ t("MONTH_TERM_POPULARITY_BADGES", { count: badgeValue }) }}
        </span>
        <span
          v-if="badgeType === badgeTypes.LOWEST_TOTAL_PAYMENT_BADGE"
          class="badge-title"
        >
          {{
            t("LOWEST_TOTAL_PAYMENT_BADGE", {
              val: formatPrice(badgeValue),
            })
          }}
        </span>
      </div>
    </div>

    <div class="payment-term-title">
      <h3>
        <div v-if="isPreApproved" class="mb-2">
          <span class="pre-approved-tag">{{ t("PRE_APPROVED") }}</span>
        </div>
        <span>{{ paymentTermTitle }}</span>
      </h3>
    </div>
    <hr />
    <div class="row justify-content-between align-items-center">
      <div class="col">
        <h5 class="payment-amount">
          {{ formatCurrencyFromMerchant(paymentAmount) }}
        </h5>
        <p v-if="isWeeklyPayment">{{ t("PER_WEEK") }}</p>
        <p v-else>{{ t("BIWEEKLY") }}</p>
      </div>
      <div v-if="monthlyPayment" class="col">
        <h5 class="monthly-payment">
          {{ formatCurrencyFromMerchant(monthlyPayment) }}
        </h5>
        <p>{{ t("PER_MONTH") }}</p>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <button class="btn pj-green-btn select" :disabled="disableButton">
          {{ t("SELECT") }}
        </button>
      </div>
      <div class="col">
        <p class="total-payment">
          {{
            `${totalCostCaption}: ${formatCurrencyFromMerchant(totalPayment)}`
          }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "@vue/runtime-core";
import { ICurrencyType } from "@/apps/merchant3/shared-hooks/useCurrency";
import { onMounted, PropType } from "vue";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import { IBadgeExperiments, IBadge } from "@/apps/merchant3/models/IStateCart";
import formatPrice from "@/apps/merchant3/shared-helpers/price-formatter.helper";
import { ApprovalState } from "@/apps/merchant3/types/OBR";
import useMerchantPersistState from "@/apps/merchant3/hooks/useMerchantPersistState";
import { PaymentFrequency } from "@/apps/merchant3/types/PaymentFrequency";
import { formatCurrencyFromMerchant } from "@/apps/merchant3/shared-helpers/format-currency.helper";
import {
  isRentalFlow,
  isSubscriptionFlow,
} from "@/apps/merchant3/helpers/checkoutFlows";

const props = defineProps({
  paymentFrequency: { type: String, default: PaymentFrequency.WEEKLY },
  months: { type: Number, response: true },
  numberOfInstallments: { type: Number, default: 0 },
  paymentAmount: { type: Number, default: 0 },
  monthlyPayment: { type: Number, default: 0 },
  totalPayment: { type: Number, default: 0 },
  preApprovalState: {
    type: String as PropType<ApprovalState>,
    default: ApprovalState.PENDING,
  },
  currency: {
    type: Object as PropType<ICurrencyType>,
    required: true,
    default: () => ({ name: "Peso Mexicano", code: "MXN", sign: "$" }),
  },
  disableButton: { type: Boolean },
  badge: {
    type: Object as PropType<IBadge>,
    default: () => ({
      badgeType: IBadgeExperiments.NO_PAYMENT_BADGE,
      badgeValue: 0,
    }),
  },
  selected: {
    type: Boolean,
    default: false,
  },
  locale: {
    type: String,
    required: true,
    default: "es-MX",
  },
});

const emit = defineEmits(["onSelect"]);

const { t } = useI18n();

const isWeeklyPayment = computed(() => {
  return props.paymentFrequency === PaymentFrequency.WEEKLY;
});
const isPreApproved = computed(() => {
  const existingPreApproved = parseInt(
    localStorage.getItem("pre_reg_selected_term") || ""
  );

  return (
    props.months == existingPreApproved &&
    props.preApprovalState === ApprovalState.APPROVED
  );
});
const selectTerm = () => {
  emit("onSelect", props.months);
};
const badgeType = ref(props.badge.badgeType);
const badgeValue = ref<number>(props.badge.badgeValue);
const badgeTypes = IBadgeExperiments;

const paymentTermTitle = computed(() => {
  if (!props.monthlyPayment) {
    return t("PAYMENT_PLAN", {
      numberOfInstallments: props.numberOfInstallments,
    });
  } else {
    switch (props.paymentFrequency) {
      case PaymentFrequency.WEEKLY:
        return t("MONTHS_PAYMENT_PLAN", {
          months: props.months,
          weeks: props.numberOfInstallments,
        });
      case PaymentFrequency.BIWEEKLY:
        return t("MONTHS_BIWEEKLY_PAYMENT_PLAN", {
          months: props.months,
          weeks: props.numberOfInstallments,
        });
    }
  }
});

const totalCostCaption = computed(() => {
  if (isRentalFlow.value) return t("RENTAL_TOTAL_COST");
  return isSubscriptionFlow.value
    ? t("TOTAL_SUBSCRIPTION_COST")
    : t("TOTAL_COST");
});

onMounted(() => {
  useMerchantPersistState(() => true);
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/constants";
.payment-term {
  border: 2px solid $pj-color-light-gray;
  border-radius: 10px;
  cursor: pointer;
  &.active {
    border: 2px solid $pj-color-green;
    background-color: $pj-color-green-hover;
    .pj-green-btn {
      background-color: $pj-color-light-silver;
      color: $pj-color-old-lavender;
      box-shadow: none;
      border: none;
    }
  }
}
.payment-term p {
  color: $pj-color-old-lavender;
}

.badge-title-background {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f2c94c;
  border-radius: 10px 10px 0 0;
  margin: -18px -18px 10px -18px;
  border: 2px solid $pj-color-light-gray;
  border-bottom: 0;
}

.active .badge-title-background {
  border: 2px solid $pj-color-green;
}
.badge-title {
  color: $pj-color-black;
  display: flex;
  align-self: center;
  font-weight: bold;
}
.pre-approved-tag {
  background-color: $pj-color-green-hover;
  color: #333;
  padding: 3px 12px;
  border-radius: 10px;
}

h3 {
  color: $pj-color-green;
  font-size: 14px;
}
h5 {
  span {
    font-size: 12px;
    font-weight: normal;
    color: $pj-color-gray;
  }
}
</style>
